/** The shapes the API returns. Mirrors reghorizon.models.Item and the API's own additions. */

/**
 * `archived` is an item she tracked and has stopped tracking (Joanne, 16 Sept 2026) — not
 * `dismissed`, which was never in her log. It leaves the log, the board paper and Actions.
 */
export type ItemStatus = "new" | "reviewed" | "tracked" | "dismissed" | "archived";

/** Every status the log's filter and Status column offer. */
export const LOG_STATUSES: ItemStatus[] = ["tracked", "new", "dismissed", "reviewed", "archived"];

/**
 * What changed on a revised page, in the model's words — `reghorizon.pagechange`.
 *
 * Not part of the item. It lives in a row of its own, is sent only on the changed-pages list,
 * and only while it describes the change the item is carrying; it is removed when she decides.
 * `state: "unreadable"` is an answer, not a failure — the page may have gone.
 */
export interface ChangeSummary {
  changed_on: string;
  state: "ready" | "unreadable";
  what_changed: string[];
  significance: "substantive" | "minor" | "none_found" | "unclear";
  log_note: string;
  suggested_action: string;
  /** What was compared — the sentence she weighs the answer by. */
  compared_against: string;
  notes: string[];
  model: string;
  generated_at: string;
  read_on: string;
}

/** Her three choices on a revised page. No dismiss: the item is already in her log. */
export type PageUpdateDecision = "update" | "update_and_action" | "stop_tracking";

export interface PageUpdate {
  decision: PageUpdateDecision;
  /** The change date her screen showed. The API refuses the decision if the page moved again. */
  changed_on: string;
  /** Added in front of her Summary, stamped with the change date. */
  note: string;
  /** File it under the date the page now carries. */
  refile: boolean;
  next_step?: string;
  /** Absent leaves her date alone; "" clears it. */
  due_date?: string;
}

export interface AuditEntry {
  at: string;
  by: string;
  changes: Record<string, { from: unknown; to: unknown } | string>;
}

export interface Item {
  id: string;
  title: string;
  url: string;
  canonical_url: string;
  regulator: string;
  source_id: string;
  source_name: string;
  category: string;
  published_at: string | null;
  captured_at: string;
  month_key: string;
  summary: string;
  status: ItemStatus;
  clients: string[];
  raw_ref?: string | null;

  // Joanne's own columns.
  reference: string;
  key_points: string;
  applies_to: string;
  link_text: string;
  next_steps: string;
  next_steps_status: "" | "open" | "complete";
  /** When the next step is due. Hers — a suggestion is not this until she accepts it. */
  due_date: string;
  /** How much it matters, apart from when it is due. "" means she has not said — read as medium. */
  priority?: Priority;
  norwood_action: string;
  relevance_note: string;
  /** Her own subject tags, as distinct from `ai_tags`, which are only suggestions. */
  tags: string[];
  board_flag: boolean;

  /**
   * What the AI suggested. Kept apart from her own fields on purpose: `category` and
   * `clients` are her decisions, and nothing here is applied until she applies it.
   */
  ai_summary?: string;
  ai_category?: string;
  ai_clients?: Record<string, "yes" | "maybe" | "no">;
  ai_rationale?: Record<string, string>;
  ai_tags?: string[];
  ai_model?: string;
  ai_enriched_at?: string;
  /** A suggested deadline, and the words it was read from. */
  ai_due_date?: string;
  ai_due_date_basis?: string;
  /**
   * Why `ai_due_date` is empty when it is. `dated` — read from her note. `unresolved` — her
   * note names a deadline nobody could pin to a day, so it needs her. `none` — no deadline.
   * Without this the last two look identical, and the one needing her disappears.
   */
  ai_due_date_status?: "dated" | "unresolved" | "none" | "";
  regulator_raw: string;
  regulators: string[];
  clients_raw: string;
  import_raw?: unknown[];

  /**
   * A source has re-announced this page with a later date than the one it is filed under —
   * the regulator saying the page changed, not a repeat.
   *
   * The system never moves the date: `published_at` decides `month_key` and the log's sort
   * order, so an item silently changing month is a far bigger blast radius than a stale date.
   * The change is surfaced in the review queue, and she can re-file it there herself.
   *
   * `page_change_ack` holds the change date she decided on, not a boolean, so a page that
   * changes again re-appears on its own. Whether one is outstanding is decided by the API
   * (`reghorizon.changes.has_unseen_change`) and never re-derived here.
   */
  page_changed_at?: string | null;
  page_change_seen_at?: string | null;
  page_change_source?: string | null;
  page_change_ack?: string | null;
  /**
   * Hers: never report revisions to this page. Some pages are revised for reasons that are
   * not regulatory change, and a band reporting those weekly is one she stops reading.
   * Suppresses the notice, never the record — unmuting shows the page's current state.
   */
  page_change_muted?: boolean;
  /** Only on `/items/changed`: what changed, or null while it is still being written. */
  change_summary?: ChangeSummary | null;

  // Added by the API on the Actions view, so the flag and the sort cannot disagree.
  overdue?: boolean;
  days_until?: number | null;

  // Added by the API on write.
  updated_at?: string;
  updated_by?: string;
  audit?: AuditEntry[];
  already_existed?: boolean;
}

/**
 * What kind of firm a client is — the facts an impact assessment is calibrated against.
 *
 * Deliberately not a register of their controls: Joanne does not hold one, and an assessment
 * directs a firm to act rather than auditing what it already has. The negatives carry as much
 * weight as the positives — "no client money permission", "not a TCSP" — because they are what
 * license an assessment to rule provisions out in writing.
 */
export interface FirmProfile {
  business_model?: string;
  jurisdictions?: string[];
  size_band?: string;
  stage?: string;
  authorised_on?: string;
  /** Three-way, not a boolean: "no" is an assertion, unset means nobody has said. */
  client_money?: string;
  permissions_not_held?: string[];
  /** What a pre-authorisation firm is applying for. The Register cannot supply this. */
  permissions_sought?: string[];
  exclusions?: string[];
  /**
   * What the firm intends to start doing — the only field here that RAISES relevance.
   * "They do not currently hold client money but that is on their roadmap" is neither yes,
   * no, nor unset, and a firm about to hold client money should be reading CASS now.
   */
  roadmap?: string[];
  volumes?: string;
  prepared_for?: string[];
  owner_titles?: string[];
  proportionality_anchor?: string;
}

export const SIZE_BANDS = [
  "",
  "sole trader",
  "2-9 employees",
  "10-49 employees",
  "50-249 employees",
  "250+ employees",
] as const;

/**
 * Pre-authorisation is a first-class state, not a gap: Joanne keeps a profile on every customer
 * because getting the unregulated ones regulated is the engagement. "Not seeking authorisation"
 * is a different thing again — a software vendor selling into regulated firms needs to know how
 * a change affects its customers, not itself.
 */
export const STAGES = [
  "",
  "pre-authorisation",
  "newly authorised",
  "established",
  "not seeking authorisation",
] as const;

export const CLIENT_MONEY = ["", "yes", "no", "unknown"] as const;

/** The fields an assessment cannot be generated without. Mirrors reghorizon.firmprofile. */
export const REQUIRED_FOR_ASSESSMENT: (keyof FirmProfile)[] = [
  "business_model",
  "jurisdictions",
  "size_band",
  "stage",
];

export function missingForAssessment(profile?: FirmProfile): (keyof FirmProfile)[] {
  if (!profile) return [...REQUIRED_FOR_ASSESSMENT];
  return REQUIRED_FOR_ASSESSMENT.filter((field) => {
    const value = profile[field];
    if (Array.isArray(value)) return value.length === 0;
    return !((value as string) || "").trim();
  });
}

/**
 * One line of a client's rulebook scope, already resolved against the Handbook tree.
 *
 * `reference` and `tier` are hers. The rest was read out of a tree snapshot by
 * `reghorizon.handbook.scope`, which is the only thing allowed to turn a reference into an
 * entity id — so an id here is a record of what resolved, not a licence to fetch it. Editing
 * happens by re-running the resolver (`make handbook-scope`), because only that has a tree.
 */
export interface ScopeEntry {
  reference: string;
  entity_id: string;
  name: string;
  type: "" | "blocks" | "sourcebook" | "chapter" | "section";
  tier?: string;
}

/**
 * One Handbook node a search matched. `url` is the FCA's own page.
 *
 * Note the FCA site is an Angular SPA, so every path returns 200 — a link built from an
 * unvalidated reference looks perfectly healthy and shows nothing. These urls are only ever
 * built server-side from an entity id read out of a real tree.
 */
export interface HandbookHit {
  reference: string;
  name: string;
  type: string;
  entity_id: string;
  url: string;
}

export interface HandbookSearch {
  query: string;
  exact: boolean;
  count: number;
  items: HandbookHit[];
}

/** One development as a board paper would present it. */
export interface PaperLine {
  title: string;
  reference: string;
  regulator: string;
  url: string;
  note: string;
  /** Whether the relevance note is her wording or the model's — what she is checking. */
  note_is_hers: boolean;
  next_steps: string;
  due_date: string;
}

/** What a board paper would contain, built without writing one or drafting a summary. */
export interface BoardPaperPreview {
  client: string;
  month: string;
  is_empty: boolean;
  item_count: number;
  sections: { regulator: string; items: PaperLine[] }[];
  open_actions: PaperLine[];
  horizon: PaperLine[];
}

export interface Client {
  id: string;
  name: string;
  status: "draft" | "active" | "archived";
  website?: string;
  frn?: string;
  scope_profile?: string;
  notes?: string;
  firm_profile?: FirmProfile;
  handbook_scope?: ScopeEntry[];
  updated_at?: string;
  updated_by?: string;
  audit?: AuditEntry[];
}

export interface Source {
  id: string;
  name: string;
  type: string;
  regulator: string;
  schedule: string;
  /** The registry's answer: is there a working fetcher, switched on in sources.yaml? */
  available: boolean;
  /** Hers: does she want this feed in her queue? Absent state means yes. */
  user_enabled: boolean;
  /** What actually happens — available and wanted. */
  enabled: boolean;
  /** Items captured while it was switched off, waiting for her to want them. */
  held_count: number;
  notes?: string;
  last_run_at?: string;
  last_ok_at?: string;
  last_item_at?: string;
  last_error?: string;
  last_new_items?: number;
  total_new_items?: number;
  last_status?: string;
  /** The registry's own note: why a feed is off, or what is wrong with it. */
  url?: string;
}

/**
 * Items captured per source per day. Capture date, not publication date — the question is
 * whether the feed is alive, and a working feed routinely delivers last week's publications.
 */
export interface SourceActivity {
  /** Oldest first, ISO dates, UK days. */
  days: string[];
  /** source id -> ISO day -> count. Missing means zero. */
  counts: Record<string, Record<string, number>>;
}

/**
 * A change to one of her subscriptions — a confirmation request, or notice that a
 * subscription has ended. It arrives by email like anything else, but it alters what she
 * sees of the regulatory world, so it waits for her decision rather than taking effect.
 */
export interface SubscriptionEvent {
  message_id: string;
  kind: "confirmation" | "unsubscribe";
  approval: "pending" | "approved" | "rejected";
  subject: string;
  from_address: string;
  from_domain: string;
  received_at: string;
  status: "accepted" | "rejected";
  authenticated?: boolean;
  raw_ref?: string;
  decided_by?: string;
  decided_at?: string;
  approval_note?: string;
}

export interface Page<T> {
  items: T[];
  count: number;
  cursor?: string;
}

/**
 * The Actions view. The counts come from the server so the badge and the list agree, and
 * so the digest — which has no browser — computes "overdue" exactly the same way.
 */
/**
 * One piece of work, on one item, optionally for one client.
 *
 * An action was a property of an item until 20 September 2026 — one Next steps cell, one status,
 * one date — because that is the shape of Joanne's workbook. She asked what to do when she has
 * marked something done and then wants to action it for another client, and there was no answer
 * that kept both facts. The board paper had it worse: filtered by client, reading one shared
 * cell, it printed the same step in all three papers and emptied all three at once.
 *
 * `client` is a **name**, matching the strings in the item's `clients`, and `""` is a real value
 * meaning nobody in particular — which is what all 176 of her pre-existing next steps became.
 */
export interface Action {
  id: string;
  item_id: string;
  client: string;
  next_steps: string;
  next_steps_status: "" | "open" | "complete";
  due_date: string;
  created_at: string;
  updated_at: string;
}

/**
 * An action as the Actions screen gets it: its item's fields with the action's own laid over.
 *
 * The server sends this shape so that everything already written against an item — the title,
 * the link, the priority, the suggested deadline and its basis — goes on working unchanged. Only
 * `action_id` and `client` are new, and `next_steps`, `next_steps_status` and `due_date` are the
 * action's rather than the item's summary of all of them.
 */
export interface ActionView extends Item {
  action_id: string;
  client: string;
  overdue?: boolean;
  days_until?: number | null;
}

/** What one action may be changed to. The step itself cannot be emptied — delete it instead. */
export type ActionPatch = Partial<
  Pick<Action, "client" | "next_steps" | "next_steps_status" | "due_date">
>;

export interface ActionsPage extends Page<ActionView> {
  /**
   * How many are still to do. Distinct from `count`, which is how many rows came back — with
   * "Show completed" ticked those differ, and it is this one the tab badge and the header
   * read. Every count below is over the open set for the same reason: a thing marked done is
   * not overdue and does not need a date.
   */
  open: number;
  overdue: number;
  unresolved: number;
}

/**
 * Editable item fields, as the API's whitelist defines them.
 *
 * `next_steps`, `next_steps_status` and `due_date` are **not** here, and their absence is the
 * type doing real work. Since 20 September 2026 those three are derived from the item's actions
 * and the patch route refuses them with a 400 — so a screen that still sent one would fail at
 * runtime on a click rather than here. See `Action`.
 */
export type ItemPatch = Partial<
  Pick<
    Item,
    | "status"
    | "title"
    | "summary"
    | "key_points"
    | "applies_to"
    | "priority"
    | "norwood_action"
    | "relevance_note"
    | "reference"
    | "category"
    | "link_text"
    | "clients"
    | "tags"
    | "regulators"
    | "board_flag"
    | "page_change_muted"
  >
>;

/**
 * A manual add: any patchable field, plus the publication date.
 *
 * `published_at` is settable only here. It decides the item's month and the log's sort order,
 * so it is deliberately not part of `ItemPatch` — a feed item silently changing month is a
 * much bigger blast radius than the one thing this fixes, which is a hand-added item being
 * dated today when the article is three weeks old.
 */
export type NewItemFields = ItemPatch & { published_at?: string };

export const NORWOOD_ACTIONS = ["", "Review impact", "Watching brief"] as const;

/**
 * "All" is a tag, not a client — her shorthand for every client, and it was on well over half
 * the rows she had tagged. It is deliberately not a client record, so it has to be offered
 * alongside them.
 *
 * **It means exactly what selecting every client means.** Not a standing rule that catches
 * clients onboarded later: a shorthand for the ticks she would otherwise make one by one. The
 * server resolves the word to client names on the way in (`reghorizon.clients.resolve`), so
 * nothing here has to send it — the picker sends names and this is only ever how a selection
 * of everyone is *read back*.
 */
export const ALL_CLIENTS_TAG = "All";

/** The client tags to offer, before any client records exist and after. */
/**
 * What the AI concluded about an item, as one value she can filter on.
 *
 * `none` is the useful one: an enriched item the model judged relevant to nobody. Over a
 * backlog those are the bulk of it, and being able to see only those — and clear them
 * together — is the difference between an afternoon and ten minutes.
 */
export type AiVerdict = "suggested" | "maybe" | "none" | "unenriched";

export function aiVerdict(item: Item): AiVerdict {
  if (!item.ai_enriched_at) return "unenriched";
  const verdicts = Object.values(item.ai_clients ?? {});
  if (verdicts.includes("yes")) return "suggested";
  if (verdicts.includes("maybe")) return "maybe";
  return "none";
}

export const AI_VERDICT_LABELS: Record<AiVerdict, string> = {
  suggested: "Suggested for a client",
  maybe: "Possibly relevant",
  none: "Relevant to nobody",
  unenriched: "Not yet summarised",
};

/** A verdict the AI offered on one client, with the reason it gave. */
/**
 * A client's live entry on the FCA's Financial Services Register.
 *
 * Deliberately loose: the Register returns different fields for different firms, and a
 * shape declared field-by-field here would quietly hide whatever it did not anticipate.
 * Rows are rendered as whatever they contain.
 */
export interface RegisterSection {
  name: string;
  rows: Record<string, unknown>[];
  note: string;
}
export interface RegisterFirm {
  client_id: string;
  frn: string;
  name: string;
  status: string;
  fetched_at: string;
  url: string;
  sections: RegisterSection[];
}
export interface ClientSuggestion {
  name: string;
  verdict: "yes" | "maybe";
  why: string;
}

/**
 * The suggestions worth showing her, strongest first. "no" verdicts are deliberately not
 * offered as chips — the rationale for them is stored and available, but a row cluttered
 * with every client the item does NOT apply to is noise at triage speed.
 */
export function clientSuggestions(item: Item): ClientSuggestion[] {
  const verdicts = item.ai_clients ?? {};
  const rank = { yes: 0, maybe: 1 } as const;
  return Object.entries(verdicts)
    .filter(([, v]) => v === "yes" || v === "maybe")
    .map(([name, v]) => ({
      name,
      verdict: v as "yes" | "maybe",
      why: item.ai_rationale?.[name] ?? "",
    }))
    .sort((a, b) => rank[a.verdict] - rank[b.verdict] || a.name.localeCompare(b.name));
}

/**
 * The clients a tag can name: everything not archived, in the order the Clients screen lists
 * them.
 *
 * "Live" is `status !== "archived"`, never `status === "active"`. The three real clients were
 * `draft` for months while Joanne sharpened her scope profiles, and a test for "active" would
 * have offered her nobody and done it silently. They went `active` on 31 August 2026, which
 * makes the rule look redundant and is exactly when it stops being: the next client she adds
 * starts as a draft. The fallback covers the first render, before `/clients` answers.
 */
export function liveClients(clients: Client[]): string[] {
  const names = clients
    .filter((client) => client.status !== "archived")
    .map((client) => client.name);
  return names.length ? names : ["Finspector", "Faciit", "Aveni"];
}

/**
 * Whether this selection is every live client and nothing else.
 *
 * Exact, not "covers them all": an item naming the three live clients **and** archived
 * Senguard is not "All", because collapsing it to that word on screen would hide Senguard
 * from the only place she can see it. Mirrors `reghorizon.clients.is_everyone`.
 */
export function isEveryone(names: string[], live: string[]): boolean {
  if (!live.length || names.length !== live.length) return false;
  return live.every((name) => names.includes(name));
}

/**
 * Whether every live client is selected, ignoring anything else on the item.
 *
 * A different question from {@link isEveryone}, and they were the same function until a row
 * naming archived Senguard proved they could not be. That row can have all three live clients
 * ticked, so the "All clients" box must show as ticked and must clear them when pressed —
 * under the exact test it read as unticked and pressing it did nothing, because
 * `[...live, "Senguard"]` is not `live`. Reading is exact (or "All" would hide Senguard);
 * ticking is a subset.
 */
export function coversLive(names: string[], live: string[]): boolean {
  return live.length > 0 && live.every((name) => names.includes(name));
}

/**
 * Her Client applicability cell, as she would write it — "All" for a selection of everyone,
 * the names otherwise.
 *
 * Derived rather than stored, which is the point: an item where she pressed "All clients" and
 * one where she ticked all three by hand are the same fact, so they must not read differently
 * in the grid, the export or the digest. Mirrors `reghorizon.clients.label`.
 */
export function clientLabel(names: string[], live: string[]): string {
  return isEveryone(names, live) ? ALL_CLIENTS_TAG : names.join(", ");
}

/**
 * Tick or untick one client tag, with her shorthand acting as a master toggle.
 *
 * One function because three screens offer these tags — the log's picker, the review queue's
 * chips and the Add dialog's — and "All" behaving differently on one of them is the drift this
 * whole rule exists to remove. Pressing "All" when everyone is already selected clears them,
 * which is what a ticked box does; anything on the item that is *not* a live client (an
 * archived one she tagged years ago) is untouched either way, because that is her history and
 * not part of what the shorthand covers.
 */
export function toggleClient(current: string[], name: string, live: string[]): string[] {
  if (name !== ALL_CLIENTS_TAG) {
    return current.includes(name) ? current.filter((n) => n !== name) : [...current, name];
  }
  const kept = current.filter((n) => !live.includes(n));
  return coversLive(current, live) ? kept : [...live, ...kept];
}

/** The client tags to offer as chips, with her shorthand in front of the names. */
export function clientTags(clients: Client[]): string[] {
  return [ALL_CLIENTS_TAG, ...liveClients(clients)];
}

/**
 * The regulators worth offering as a dropdown. The field stays open — her log carries
 * combinations and one-off bodies — so this is a convenience, not a constraint.
 */
/** How much an action matters. "" is unset, which reads as medium and is never stored. */
export type Priority = "high" | "medium" | "low";

export const REGULATORS = [
  "FCA",
  "PRA",
  "BoE",
  "HMT",
  "PSR",
  "FOS",
  "ICO",
  "EBA",
  "ESMA",
  "OFSI",
  "FRC",
  "DRCF",
  "FPC",
  "CMORG",
  "FSB",
  "Legislation",
] as const;

/**
 * One impact assessment run, from the click to the draft.
 *
 * The record is the contract with the worker: the API accepts, returns this at `queued`, and the
 * screen polls until it reaches a terminal status. `refused` and `failed` are deliberately
 * different — the first is the system declining on purpose, with a sentence she can act on, and
 * the second is something broken that is Craig's problem.
 */
export type AssessmentStatus = "queued" | "generating" | "ready" | "refused" | "failed";

export type AssessmentProblem =
  | "too_long"
  | "too_many_obligations"
  | "over_budget"
  | "over_time"
  | "stalled"
  | "other";

export type AssessmentMode = "whole" | "passages" | "sections";

export interface AssessmentSection {
  index: number;
  title: string;
  characters: number;
}

export interface Assessment {
  id: string;
  item_id: string;
  client_id: string;
  client: string;
  item_title: string;
  status: AssessmentStatus;
  requested_by: string;
  requested_at: string;
  updated_at: string;
  month: string;
  reference: string;
  filename: string;
  summary: string;
  counts: Record<string, number>;
  /** Quotes the source did not contain. Reported, never silently dropped. */
  rejected: { label: string; quote: string; reason: string }[];
  /** References that render as `[unverified: …]` in the document. */
  unresolved: { text: string; note: string; resolved: boolean }[];
  /** Ways the document disagrees with itself. Should always be empty. */
  problems: string[];
  notes: string[];
  message: string;
  needs_attention: boolean;
  /**
   * What this run cost, in pence. Converted by the API from the US cents it is billed and
   * stored in, so nothing rounds twice.
   *
   * It is what separates the two kinds of failure on screen. A run that broke before it spent
   * anything can honestly be re-tried; one stopped part-way by its own budget bought Opus,
   * produced nothing, and would do it again — so the screen must not offer a free retry.
   */
  cost_pence?: number;
  /**
   * Why it produced nothing, as a code the screen acts on — empty while running or once ready.
   * Derived by the API for runs from before codes were stored, so old ones get the same buttons.
   */
  problem?: AssessmentProblem | "";
  /** Too much to read in one go: offer sections she picks, or passages the AI picks. */
  can_narrow?: boolean;
  /** What this run reads. */
  mode?: AssessmentMode;
  sections?: number[];
  /** The publication's sections, saved by a run that was too much, for her to choose from. */
  outline?: AssessmentSection[];
  /** Set while the worker lists the sections of a run that stopped before it could. */
  outline_requested_at?: string;
  /** Where a running job has got to, and since when. Moves as each stage starts. */
  stage?: string;
  stage_label?: string;
  stage_number?: number;
  stage_count?: number;
  stage_started_at?: string;
  started_at?: string;
  /** How many runs are in flight ahead of a queued one. The worker runs one at a time. */
  ahead?: number;
  dismissed_at?: string;
  /** Minted at read time, so a stale record never shows a dead link. Empty unless ready. */
  url?: string;
  /**
   * Queued or generating, and not moving. Derived by the API at read time, never stored — a
   * Lambda timeout kills the process, so the thing that would have written a terminal status is
   * the thing that died. Without this the screen spins for ever on a run that ended in silence.
   */
  stalled?: boolean;
  /**
   * The run finished but its .docx has aged out of S3. Checked at read time: presigning verifies
   * nothing, so without this the screen shows a normal download link for a file deleted weeks
   * ago. The run's record is unaffected — only the copy has gone.
   */
  expired?: boolean;
}

/** What a run would work from, and what stands in its way. Nothing is spent to find out. */
export interface AssessmentPreview {
  item: {
    id: string;
    title: string;
    url: string;
    regulator?: string;
    category?: string;
    published_at?: string;
    reference?: string;
    norwood_action?: string;
  };
  /**
   * How she has filed the row — the clients tagged on it and her own tags.
   *
   * A **reminder** printed on the document, never an input to it. There is no client block any
   * more: since 31 August 2026 an assessment is of a publication, and nothing about a client
   * chooses, gates or changes a word of it.
   */
  reminder: { clients: string[]; tags: string[] };
  existing: Assessment | null;
  month: {
    used: number;
    cap: number;
    /**
     * All in **pence**. What a model call costs is priced and stored in US dollars — that is what
     * Anthropic bills — and the API converts once, at the edge, at `rate`. The rate travels with
     * the figures and is printed in the small print, so a converted number is never passed off as
     * a fact.
     */
    budget_pence: number;
    spent_pence: number;
    remaining_pence: number;
    /** Dollars to pounds, as the API converted them. */
    rate: number;
  };
  /** What this run is predicted to cost, and where that figure came from. */
  estimate: {
    pence: number;
    /** How it was arrived at. Shown with the number: a prediction nobody can judge is noise. */
    basis: string;
    /** False when nothing has ever been measured and this is a stated default. */
    measured: boolean;
    /**
     * The most one run can spend before the guard stops it — the number she can rely on.
     *
     * `pence` is a median of past runs and knows nothing about how long *this* publication is,
     * so on a 400,000-character consultation it quoted about £1 against a run that spent £7.72.
     * The prediction cannot be fixed without fetching the document, which the preview
     * deliberately does not do. The ceiling can be stated, so it is.
     */
    ceiling_pence: number;
  };
  can_run: boolean;
  blockers: string[];
  warnings: string[];
  cost: string;
}


/** One document the system has produced: a workbook, a board paper, or an assessment draft. */
export interface GeneratedDocument {
  filename: string;
  /**
   * How this document is named back to the API — the S3 key with the `exports/` prefix taken
   * off. Deliberately not the key: the browser cannot express a path outside that prefix, so
   * the delete route has nothing to defend against beyond a malformed ref.
   */
  ref: string;
  /** Read from the key, not stored — see the API's `documents.py`. */
  kind: "assessment" | "board paper" | "export" | "document";
  size: number;
  created_at: string;
  /** Minted when the list is read, so a stale row never shows a dead link. */
  url: string;
}

export interface DocumentsPage {
  items: GeneratedDocument[];
  count: number;
  /** How many exist, which can exceed how many are shown. */
  total: number;
  truncated: boolean;
  expires_in: number;
}

/**
 * What a pasted link turns out to be, read before it becomes an item.
 *
 * The provenance fields are the point rather than trimming. `published_basis` names the
 * metadata the date came from and `due_date_basis` quotes the words the deadline was read
 * out of, because she is being asked to accept both in the second before she presses Add,
 * and a date with no visible origin is one she has to go and check herself — which is the
 * work this was meant to remove.
 */
export interface ItemPreview {
  /** The URL is already in the log. Everything else is absent; nothing was fetched or spent. */
  already_existed: boolean;
  /** Present only when `already_existed`. */
  item?: Item;
  url: string;
  title: string;
  /**
   * The date the item is filed under: **when the page was last updated**, falling back to
   * when it first appeared. Joanne's call — what she needs to see is the most recent state
   * of the document, not the day a two-year-old page happened to be born.
   *
   * "" when the page states neither, in which case today's is used and `notes` says so.
   */
  published_at: string;
  /**
   * Which date it is and where it was read: "last updated, per schema.org dateModified:
   * 10/07/2026". The first half is the half she reads — whether she is looking at the most
   * recent state of the document was the whole of the request.
   */
  published_basis: string;
  summary: string;
  category: string;
  /** The clients the model would tag — its "yes" verdicts only, never a "maybe". */
  clients: string[];
  rationale: Record<string, string>;
  tags: string[];
  due_date: string;
  due_date_basis: string;
  /** A next step to put against that deadline, for her to reword. "" when there is none. */
  next_steps: string;
  /** False when the page could not be read or the model could not be reached. */
  enriched: boolean;
  /** Anything she should know before pressing Add — a missing date, an unreadable page. */
  notes: string[];
}


/**
 * One regulatory framework a client's proportionality basis can name.
 *
 * `aliases` is the point of the record: nobody spells these the same way. Joanne writes
 * "MLR 2017", an instrument writes "the Money Laundering, Terrorist Financing and Transfer of
 * Funds (Information on the Payer) Regulations 2017", and the FCA writes "the Money Laundering
 * Regulations". An assessment cites a client's basis only where the publication uses one of
 * these, so a missing spelling means a citation that should appear and does not.
 */
export interface Framework {
  id?: string;
  name: string;
  title: string;
  aliases: string[];
  updated_at?: string;
  updated_by?: string;
}

export type FrameworkInput = Pick<Framework, "name" | "title" | "aliases">;

export interface FrameworksPage {
  items: Framework[];
  count: number;
  /**
   * False while the table is empty and the packaged file is standing in. The list is real either
   * way; only one of the two can be edited, and the screen says which.
   */
  seeded: boolean;
}
